import React, { useRef, useState, useEffect } from "react";
import { isMobile } from "../common";
import Dropdown from "./Dropdown";
export function FilterTab({
  options,
  selected,
  onClick,
  isSecondary,
  onResultOptions,
  isRounded,
  isSingle,
  isSmall,
  allowMultiple = false,
  disabledOptions = [],
  variant = "pill-outline",
}: {
  options: string[];
  selected: string[];
  isSecondary?: boolean;
  isRounded?: boolean;
  isSmall?: boolean;
  isSingle?: boolean;
  allowMultiple?: boolean;
  onClick?: (option: string, index: number) => void;
  onResultOptions?: (options: string[]) => void;
  disabledOptions?: string[];
  variant?: "default" | "pill-outline" | "pill-filled";
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });

  const activeIndex = options.findIndex(
    (opt) => selected.includes(opt) && options.length !== selected.length,
  );

  useEffect(() => {
    if (containerRef.current && activeIndex !== -1) {
      const buttons = Array.from(
        containerRef.current.querySelectorAll("button"),
      ) as HTMLElement[];
      const activeBtn = buttons[activeIndex];
      if (activeBtn) {
        setIndicatorStyle({
          left: activeBtn.offsetLeft,
          width: activeBtn.offsetWidth,
          opacity: 1,
        });
      }
    } else {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [activeIndex, options, selected, variant]);

  const handleSelection = (option: string, index: number) => {
    // Check if the option is disabled
    if (disabledOptions.includes(option)) {
      return; // Do nothing if option is disabled
    }

    if (onClick) {
      onClick(option, index);
    }
    if (onResultOptions) {
      if (selected.includes(option)) {
        if (selected.length === 1) {
          onResultOptions(options); // Reset to all options if only one is selected
        } else {
          onResultOptions(selected.filter((item) => item !== option)); // Remove the selected option
        }
      } else {
        onResultOptions([...selected, option]); // Add new option to selection
      }
    }
  };

  return isSingle && isMobile ? (
    <Dropdown
      options={options}
      selected={selected[0]}
      onSelect={(option: string, index: number) => {
        handleSelection(option, index); // Use the same logic here
      }}
    />
  ) : (
    <div
      id="trader-tab"
      ref={containerRef}
      className={`relative z-0 ${
        variant !== "default"
          ? "inline-flex items-center gap-1 bg-gray-100 p-[1px] rounded-full shadow-inner"
          : "flex"
      }`}
    >
      {/* Sliding Indicator */}
      {activeIndex !== -1 && (
        <div
          className={`absolute transition-all duration-300 ease-in-out ${
            variant !== "default"
              ? variant === "pill-outline"
                ? "border border-[#f1935c] bg-white rounded-full shadow-sm"
                : "bg-[#f1935c] rounded-full"
              : "bg-[#f1935c]" +
                (isRounded ? " rounded-[16px]" : " rounded-[8px]")
          }`}
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
            height: variant !== "default" ? "calc(100% - 2px)" : "100%",
            top: variant !== "default" ? "1px" : "0",
            opacity: indicatorStyle.opacity,
            zIndex: -1,
          }}
        />
      )}

      {options.map((option, index) => {
        const isDisabled = disabledOptions.includes(option);
        const isSelectedActive =
          selected.includes(option) && options.length !== selected.length;

        return (
          <button
            key={option}
            onClick={() => handleSelection(option, index)}
            disabled={isDisabled}
            style={{
              // Make background transparent when active so the sliding div shows
              ...(isSelectedActive
                ? {
                    backgroundColor: "transparent",
                    borderColor:
                      variant === "pill-outline" ? "transparent" : undefined,
                  }
                : {}),
            }}
            className={
              variant !== "default"
                ? "tab-pill " +
                  (isSelectedActive
                    ? variant === "pill-outline"
                      ? "tab-pill-outline-active shadow-none bg-transparent"
                      : "tab-pill-filled-active bg-transparent"
                    : "") +
                  (isDisabled
                    ? " opacity-50 cursor-not-allowed"
                    : " cursor-pointer hover:bg-gray-200/50")
                : (isSmall ? "tab-small " : "tab ") +
                  (isRounded ? "tab-rounded " : "") +
                  (isSelectedActive
                    ? "tab-active bg-transparent border-transparent "
                    : "") +
                  (index === 0
                    ? " tab-left "
                    : index === options.length - 1
                      ? " tab-right "
                      : "") +
                  (isSelectedActive && isSecondary
                    ? " tab-secondary bg-transparent "
                    : "") +
                  (isDisabled
                    ? " opacity-50 cursor-not-allowed bg-gray-100"
                    : " cursor-pointer")
            }
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

export function getResultFilterOptions(
  options: string[],
  currentlySelected: string[],
  selected: string,
): string[] {
  if (currentlySelected.includes(selected)) {
    if (currentlySelected.length === options.length) {
      return [selected];
    }
    if (currentlySelected.length === 1) {
      return options;
    } else {
      return currentlySelected.filter((item) => item !== selected);
    }
  } else {
    return [...currentlySelected, selected];
  }
}
